import { Box, Chip, Typography } from '@mui/material'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import React from 'react'

interface DealCardProps {
  deal: any
  orgName?: string
  onClick: () => void
}

function formatDate(d?: string) {
  if (!d) return null
  const parsed = new Date(d)
  if (!isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  return d
}

function formatCurrency(val?: number | string) {
  if (val === null || val === undefined || val === '') return null
  const num = typeof val === 'string' ? parseFloat(val) : val
  if (isNaN(num)) return null
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

const DealCard: React.FC<DealCardProps> = ({ deal, orgName, onClick }) => {
  const statusColor = renderColorByString(deal?.stage)
  const typeColor = renderColorByString(deal?.type)
  const dealStatusColor = renderColorByString(deal?.dealStatus)

  const startDate = formatDate(deal?.startDate)
  const endDate = formatDate(deal?.endDate)
  const total = formatCurrency(deal?.total)

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        px: 1.5,
        py: 1,
        my: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        maxWidth: 320,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-1px)',
          boxShadow: 3,
        },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.3, color: 'text.primary' }}>
        {deal?.name || 'Unknown Deal'}
      </Typography>

      {orgName && (
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2, fontSize: '0.7rem' }}>
          {orgName}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
        {deal?.type && (
          <Typography
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
              color: typeColor,
            }}
          >
            {deal.type}
          </Typography>
        )}
        {deal?.stage && (
          <Typography
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
              color: statusColor,
            }}
          >
            {deal.stage}
          </Typography>
        )}
        {deal?.dealStatus && (
          <Typography
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
              color: dealStatusColor,
            }}
          >
            {deal.dealStatus}
          </Typography>
        )}
        {deal?.semanticVersion && (
          <Chip
            label={`v${deal.semanticVersion}`}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 700,
              fontFamily: 'monospace',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
      </Box>

      {(startDate || endDate || total) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
          {(startDate || endDate) && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', lineHeight: 1.3 }}>
              {startDate && endDate ? `${startDate} – ${endDate}` : startDate || endDate}
            </Typography>
          )}
          {total && (
            <Typography
              variant="caption"
              sx={{
                color: deal?.total > 0 ? 'text.success' : 'text.primary',
                fontSize: '0.65rem',
                fontWeight: 600,
                lineHeight: 1.3,
              }}
            >
              {total}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

export default DealCard
